
import {UHttp} from "../../utils/UHttp"

export interface UpdateCompanyResponse {
    success: boolean;
    message: string;
    status: number;
    url?: string; // Pour le logo
    urls?: string[]; // Pour la galerie
}

export interface SirenVerificationResponse {
    success: boolean;
    isValid: boolean;
    data: any;
    companyUpdated?: boolean;
    message?: string;
    status?: number;
}

export interface CompanyAddFormData {
    businessName: string;
    description: string;
    domain: string;
    phone: string;
    email: string;
    address: string;
    city: string;
    postalCode: string;
    country: string;
    siren: string;
    sirenVerified: boolean;
    reservationConditions: string;
    paymentMethods: string[];
    profilePicture: string | null;
    galleryPhoto: File[] | string[];
    logo: File | null;
}

export interface CompanyEditFormData extends Omit<Company, 'createdAt' | 'updatedAt'> {
    logo?: File | null;
}

export interface collaboratorsByCompany {
    collaboratorCompagnieId: string; // ID de collaborator_compagnies (PRIMARY KEY)
    collaboratorId: string; // ID du collaborateur/user (FOREIGN KEY)
    firstName: string;
    lastName: string;
    email: string;
    profession?: string;
    role: 'OWNER' | 'ADMIN' | 'STAFF';
    owner: boolean;
    avatar?: string;
}

export interface Company {
    id: string;
    businessName: string;
    description: string;
    phone: string;
    email: string;
    address: string;
    city: string;
    postalCode: string;
    country: string;
    siren: string;
    sirenVerified: boolean;
    companyLogo: string | null;
    paymentMethods: string[];
    reservationConditions: string;
    galleryPhoto: string | null;
    domain: string | null;
    ownerId: string;
    createdAt: Record<string, any>;  // ou Date si tu reçois une date
    updatedAt: Record<string, any>;  // idem
    collaboratorsCount: string,
    collaboratorCompanies?: collaboratorsByCompany[]
}

export interface CompaniesResponse {
    status: number,
    companies: Company[];
}

export interface SirenVerificationResponse {
    success: boolean;
    isValid: boolean;
    data: any;
    professionalUpdated?: boolean;
    message?: string;
    status?: number;
}

const apiUrlServer = process.env.REACT_APP_URL_SERVER;

export class CompanyModel {
    private static instance: CompanyModel;

    private constructor() {}

    public static getInstance(): CompanyModel {
        if (!CompanyModel.instance) {
            CompanyModel.instance = new CompanyModel();
        }
        return CompanyModel.instance;
    }

    public async handleGetAllCompaniesByCollaborator(): Promise<CompaniesResponse> {
        try {

            const response = await UHttp.get(
                apiUrlServer + "/api/company/collaborator/all",
                UHttp.authHeaders()
            )

            const responseData = await response.json();

            return {
                ...responseData,
                status: response.status
            };

        } catch (error: any) {
            console.error('Error CompanyModel.handleGetAllCompaniesByCollaborator:', error.data);
            throw error;
        }
    }

    public async handleCreateCompany(data : CompanyAddFormData): Promise<CompaniesResponse> {
        try {
            // Le serveur ne gère pas les fichiers lors de la création de l'entreprise
            const companyData = {
                businessName: data.businessName.trim(),
                description: data.description,
                domain: data.domain,
                phone: data.phone,
                email: data.email,
                address: data.address,
                city: data.city,
                postalCode: data.postalCode,
                country: data.country,
                siren: data.siren,
                sirenVerified: data.sirenVerified,
                reservationConditions: data.reservationConditions,
                paymentMethods: data.paymentMethods,
            };

            const response = await UHttp.post(
                apiUrlServer + "/api/company",
                companyData,
                UHttp.authHeaders()
            )

            const responseData = await response.json();

            return {
                ...responseData,
                status: response.status
            };

        } catch (error: any) {
            console.error('Error CompanyModel.handleCreateCompany:', error.data);
            throw error;
        }
    }

    public async handleUpdateCompany(data : CompanyEditFormData): Promise<CompaniesResponse> {
        try {
            // Trim le businessName si présent
            if (data.businessName) {
                data.businessName = data.businessName.trim();
            }

            // Filtrer les champs null pour ne pas écraser les données existantes
            const updateData = Object.fromEntries(
                Object.entries(data).filter(([_, value]) => value !== null)
            );

            const response = await UHttp.put(
                apiUrlServer + "/api/company/update/" + data.id,
                updateData,
                UHttp.authHeaders()
            )

            const responseData = await response.json();

            return {
                ...responseData,
                status: response.status
            };

        } catch (error: any) {
            console.error('Error CompanyModel.handleUpdateCompany:', error.data);
            throw error;
        }
    }

    public async handleDeleteCompany(id: string): Promise<{message: string, status: number, success: boolean}> {
        try {

            const response = await UHttp.delete(
                apiUrlServer + "/api/company/delete/" + id,
                UHttp.authHeaders()
            )

            const responseData = await response.json();

            return {
                ...responseData,
                status: response.status
            };

        } catch (error: any) {
            console.error('Error CompanyModel.handleUpdateCompany:', error.data);
            throw error;
        }
    }

    /**
         * Vérifie un numéro SIREN via l'API INPI
         * @param siren Le numéro SIREN à vérifier (9 chiffres)
         * @returns Réponse contenant le statut de validation et les données de l'entreprise
         */
        public async verifySiren(siren: string): Promise<SirenVerificationResponse> {
            try {
                const response = await UHttp.get(
                    `${apiUrlServer}/api/inpi/${siren}`,
                    UHttp.authHeaders()
                );
    
                const responseData = await response.json();
    
                return {
                    ...responseData,
                    status: response.status
                };
            } catch (error: any) {
                console.error('Error ProfessionalModel.verifySiren:', error);
                throw error;
            }
        }

        public async handleUpdateCompanyPicture(companyId: string, file: File): Promise<UpdateCompanyResponse> {
        try {
            const formData = new FormData();
            formData.append('companyId', companyId);
            formData.append('profilePicture', file); // Le serveur attend 'profilePicture'

            const response = await UHttp.putFormData(
                `${apiUrlServer}/api/company/company-picture`,
                formData,
                UHttp.authHeaders()
            );

            const responseData = await response.json();

            return {
                ...responseData,
                status: response.status
            };
        } catch (error: any) {
            console.error('Error CompanyModel.handleUpdateCompanyPicture:', error);
            throw error;
        }
    }

    /**
     * Met à jour les photos de galerie du professionnel
     * @param files Liste des fichiers images à uploader (max 5)
     * @param imageIndex Index de l'image à remplacer/supprimer (optionnel)
     * @param removeImage Indique s'il faut supprimer l'image sans la remplacer
     * @returns Réponse avec les URLs des images mises à jour
     */
    public async handleUpdateGalleryPhotos(
        companyId: string,
        files: File[],
        imageIndex?: number,
        removeImage: boolean = false
    ): Promise<UpdateCompanyResponse & { urls?: string[] }> {
        try {
            const formData = new FormData();

            // Ajouter l'ID de l'entreprise
            formData.append('companyId', companyId);

            // Ajouter chaque fichier au FormData avec le même nom de champ
            // Le serveur attend 'galleryPhotos' (avec un 's')
            files.forEach(file => {
                formData.append('galleryPhotos', file);
            });

            // Ajouter les paramètres supplémentaires si présents
            if (imageIndex !== undefined) {
                formData.append('imageIndex', imageIndex.toString());
            }

            if (removeImage) {
                formData.append('removeImage', 'true');
            }
    
            const response = await UHttp.putFormData(
                `${apiUrlServer}/api/company/gallery-photos`,
                formData,
                UHttp.authHeaders()
            );
    
            const responseData = await response.json();
    
            return {
                ...responseData,
                status: response.status
            };
        } catch (error: any) {
            console.error('Error ProfessionalModel.handleUpdateGalleryPhotos:', error);
            throw error;
        }
    }

    /**
     * Vérifie la disponibilité d'un nom d'entreprise
     * @param businessName Nom d'entreprise à vérifier
     * @param currentProfessionalId ID du professionnel actuel (optionnel, pour exclure lors de la vérification en cas de mise à jour)
     * @returns Objet indiquant si le nom est disponible avec un message
     */
    public async checkBusinessNameAvailability(businessName: string, currentProfessionalId?: string): Promise<{ available: boolean; message: string }> {
        try {
            const queryParams = new URLSearchParams();

            // Ajouter les paramètres de requête
            queryParams.append('name', businessName);
            if (currentProfessionalId) {
                queryParams.append('currentId', currentProfessionalId);
            }

            const response = await fetch(
                `${apiUrlServer}/api/company/business-name-availability?${queryParams.toString()}`,
                {
                    method: 'GET',
                    headers: UHttp.authHeaders()
                }
            );

            const responseData = await response.json();
            return responseData;
        } catch (error: any) {
            console.error('Error ProfessionalModel.checkBusinessNameAvailability:', error);
            return {
                available: false,
                message: error.message || 'Une erreur est survenue lors de la vérification du nom d\'entreprise'
            };
        }
    }

    /**
     * Récupère tous les clients qui ont réservé des services de l'entreprise
     * @param companyId ID de l'entreprise
     * @returns Liste des clients avec leurs informations
     */
    public async getCompanyClients(companyId: string): Promise<{ success: boolean; data?: any[]; message?: string }> {
        try {
            const response = await UHttp.get(
                `${apiUrlServer}/api/company/${companyId}/clients`,
                UHttp.authHeaders()
            );

            const responseData = await response.json();

            return {
                ...responseData,
                status: response.status
            };
        } catch (error: any) {
            console.error('Error CompanyModel.getCompanyClients:', error);
            return {
                success: false,
                message: error.message || 'Une erreur est survenue lors de la récupération des clients'
            };
        }
    }

}

export default CompanyModel.getInstance()